import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import {
  CopyIcon,
  EyeSlashIcon,
  FolderIcon,
  GearIcon,
  LockKeyIcon,
  PushPinIcon,
  ShareFatIcon,
  TagSimpleIcon,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";
import debounce from "lodash/debounce";

import { ICON_SIZE } from "@/config/constants";
import { EditorData } from "@/features/notes/types";
import { Editor } from "@/features/notes/components/Editor";
import { ShareNoteOption } from "@/features/notes/components/Note/ShareNoteOption";
import { SetNoteTagsOption } from "@/features/notes/components/Note/SetNoteTagsOption";
import { MoveNoteOption } from "@/features/notes/components/Note/MoveNoteOption";
import { useBoundStore } from "@/app/store";
import { hooks } from "@/services/api/hooks";
import { EditorLayout } from "@/features/notes/components/Editor/components/EditorLayout";
import { NoteActions } from "@/features/notes/components/Note/NoteActions";
import { NestedAction } from "@/shared/components/ui/NestedContentNavigator";
import { isEditorEmpty } from "@/features/notes/utils/isEditorEmpty";

export function NoteEditRoute() {
  const { noteId: noteIdFromParams } = useParams<{ folderId: string; noteId: string }>();
  const id = Number(noteIdFromParams);
  const navigate = useNavigate();
  const { userId, notes, addNote, deleteNote, tempDeleteNote, updateNote } = useBoundStore(
    useShallow((state) => ({
      userId: state.user.id,
      notes: state.notes.main,
      addNote: state.notes.addNote,
      deleteNote: state.notes.deleteNote,
      tempDeleteNote: state.notes.tempDeleteNote,
      updateNote: state.notes.updateNote,
    }))
  );

  const selectedNote = useMemo(() => notes.find((note) => note.id === id), [notes, id]);
  const { data: refetchedNote, isFetchedAfterMount } = hooks.notes.useNoteById(selectedNote?.id);

  const actionTriggeredUnmountRef = useRef<boolean>(false);
  const lastSavedDataRef = useRef<EditorData | null>(null);

  // Load note details on mount or when id/notes change
  useEffect(() => {
    if (selectedNote) {
      lastSavedDataRef.current = {
        html_content: selectedNote.html_content,
        text_content: selectedNote.text_content,
      };
    } else {
      // Only navigate if 'id' is present and 'notes' array has been populated but doesn't contain the note.
      if (id && notes.length > 0 && !notes.some((note) => note.id === id)) {
        navigate(-1);
      }
    }
  }, [id, selectedNote, notes, navigate]);

  const saveNoteContent = useCallback(
    async (data: EditorData) => {
      if (!userId || !selectedNote) {
        return;
      }

      if (lastSavedDataRef.current && lastSavedDataRef.current.html_content === data.html_content) {
        return; // No changes detected.
      }

      if (isEditorEmpty(data)) {
        // If an existing note becomes empty, delete it permanently.
        await deleteNote(selectedNote.id);
        lastSavedDataRef.current = { ...data };

        return;
      }

      // Update existing note.
      const updatedNoteData = await updateNote(selectedNote.id, data, Boolean(selectedNote.public_uuid));
      if (updatedNoteData) {
        lastSavedDataRef.current = { ...data };
      }
    },
    [userId, selectedNote, deleteNote, updateNote]
  );

  const debouncedSave = useMemo(() => debounce(saveNoteContent, 1000), [saveNoteContent]);

  useEffect(() => {
    return () => {
      if (actionTriggeredUnmountRef.current) {
        debouncedSave.cancel();
      } else {
        debouncedSave.flush();
      }
    };
  }, [debouncedSave]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        debouncedSave.flush();
        actionTriggeredUnmountRef.current = true;
        navigate(-1);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate, debouncedSave]);

  const handleEditorChange = useCallback(
    (data: EditorData) => {
      debouncedSave(data);
    },
    [debouncedSave]
  );

  const handleClose = useCallback(() => {
    debouncedSave.flush();
    actionTriggeredUnmountRef.current = true;
    navigate(-1);
  }, [navigate, debouncedSave]);

  const handleDelete = useCallback(() => {
    debouncedSave.cancel();
    if (selectedNote) {
      tempDeleteNote(selectedNote.id);
    }
    actionTriggeredUnmountRef.current = true;
    navigate(-1);
  }, [selectedNote, tempDeleteNote, navigate, debouncedSave]);

  // const contentCreationActions = useCallback((): NestedAction[] => {
  //   const actions: NestedAction[] = [];

  //   if (!selectedNote?.locked) {
  //     actions.push({
  //       content: <SpeechToText noteHasContent={!!selectedNote?.text_content} />,
  //       key: "speech-to-text",
  //       label: "Voice note",
  //       icon: <MicrophoneIcon size={ICON_SIZE.sm} />,
  //     });
  //   }

  //   return actions;
  // }, [selectedNote]);

  const actions = useCallback((): NestedAction[] => {
    const baseActions: NestedAction[] = [
      {
        key: "settings",
        label: "Settings",
        icon: <GearIcon size={ICON_SIZE.md} />,
        run: () => {
          navigate("settings", { replace: true });
        },
      },
    ];

    if (selectedNote) {
      baseActions.unshift({
        key: "delete",
        label: "Delete",
        icon: <TrashSimpleIcon size={ICON_SIZE.md} />,
        run: handleDelete,
      });
    }

    if (selectedNote && userId) {
      const noteSpecificActions: NestedAction[] = [
        {
          key: "duplicate",
          label: "Duplicate",
          icon: <CopyIcon size={ICON_SIZE.sm} />,
          run: async () => {
            await debouncedSave.flush();
            if (selectedNote.html_content !== undefined && selectedNote.text_content !== undefined) {
              addNote({
                html_content: selectedNote.html_content,
                text_content: selectedNote.text_content,
                user_id: userId,
                folder_id: selectedNote.folder_id,
              });
              navigate(-2);
            }
          },
        },
        {
          key: "tags",
          label: "Tags",
          icon: <TagSimpleIcon size={ICON_SIZE.md} />,
          content: <SetNoteTagsOption noteId={selectedNote.id} />,
        },
        {
          key: "share",
          label: "Share",
          icon: <ShareFatIcon size={ICON_SIZE.md} weight={selectedNote.public_uuid ? "fill" : "regular"} />,
          content: <ShareNoteOption noteId={selectedNote.id} />,
        },
        {
          key: "move",
          label: "Move",
          icon: <FolderIcon size={ICON_SIZE.md} />,
          content: <MoveNoteOption note={selectedNote} />,
        },
        {
          key: "pin",
          label: selectedNote.pinned ? "Unpin" : "Pin",
          icon: <PushPinIcon size={ICON_SIZE.md} weight={selectedNote.pinned ? "fill" : "regular"} />,
          run: () => {
            updateNote(selectedNote.id, { pinned: !selectedNote.pinned }, Boolean(selectedNote.public_uuid));
            navigate(-1);
          },
        },
        {
          key: "lock",
          label: selectedNote.locked ? "Unlock" : "Lock",
          icon: <LockKeyIcon size={ICON_SIZE.sm} weight={selectedNote.locked ? "fill" : "regular"} />,
          run: () => {
            updateNote(selectedNote.id, { locked: !selectedNote.locked }, Boolean(selectedNote.public_uuid));
            navigate(-1);
          },
        },
        {
          key: "mask",
          label: selectedNote.masked ? "Unmask" : "Mask",
          icon: selectedNote.masked ? (
            <EyeSlashIcon size={ICON_SIZE.sm} weight="fill" />
          ) : (
            <EyeSlashIcon size={ICON_SIZE.sm} />
          ),
          run: () => {
            updateNote(selectedNote.id, { masked: !selectedNote.masked }, Boolean(selectedNote.public_uuid));
            navigate(-1);
          },
        },
      ];
      // Prepend delete to keep it consistent if it's always first or append if it's last
      return [...noteSpecificActions, ...baseActions];
    }

    // If selectedNote is null (e.g. note not found/deleted), only return delete if it was added
    return baseActions;
  }, [selectedNote, handleDelete, userId, addNote, updateNote, debouncedSave, navigate]);

  if (!selectedNote) {
    // Note is loading or not found.
    // A loading indicator could be shown here.
    return null;
  }

  return (
    <EditorLayout
      isEditable={!selectedNote.locked}
      navbar={{
        actions: [<NoteActions actions={actions} />],
        onBack: handleClose,
      }}
    >
      <Editor
        isEditable={!selectedNote.locked}
        initialData={{
          html_content: selectedNote.html_content,
          text_content: selectedNote.text_content,
        }}
        note={selectedNote}
        refetchedNote={isFetchedAfterMount ? (refetchedNote ?? null) : null}
        onChange={handleEditorChange}
      />
    </EditorLayout>
  );
}
